import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router';
import { useEffect, useState } from 'react';
import { useAuthStore } from '@indipia/shared';
import {
  Alert,
  Button,
  ErrorState,
  Page,
  Segmented,
  Skeleton,
  StatusHint,
} from '@indipia/ui-core';
import { billingApi, type BillingCycle, type TierCode } from '../api/billingApi';
import { BILLING_CYCLE_OPTIONS, FREE_MONTHS_ANNUAL, TIER_COPY, TIER_ORDER, getTrialMonths } from '../constants/tierCopy';
import { PRICING_GRID_CLASS, PricingCard } from '../components/PricingCard';
import { calcTrialDaysLeft, resolveCtaAction } from '../lib/ctaResolution';

/** apps/shell-web ReferralLandingPage.tsx의 REFERRAL_CODE_STORAGE_KEY와 반드시 같은 값이어야
 *  한다 — 에이전트 패키지는 shell-web을 import할 수 없어(모노레포 격리 규칙) 상수를 나눠 가질 수
 *  없으므로 값만 그대로 복제해 둔다. */
const REFERRAL_CODE_STORAGE_KEY = 'referralCode';

function PricingCardsSkeleton() {
  return (
    <div className={`mt-07 ${PRICING_GRID_CLASS}`}>
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} height={420} />
      ))}
    </div>
  );
}

export function PricingPlansPage() {
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);
  const isLoggedIn = Boolean(accessToken);
  // C-8(추천인 초대) — 추천 링크를 타고 들어왔으면 ?ref= 쿼리파람을 그대로 startTrial에 실어보낸다.
  // "/u/r/{code}" 랜딩 라우트(apps/shell-web ReferralLandingPage)가 로그인 방문자를 이 파람과 함께
  // 여기로 보낸다(완료, 2026-09-02 — ReferralInvitePage.tsx 주석 참고).
  const [searchParams] = useSearchParams();
  // 미로그인 방문자는 ReferralLandingPage가 코드를 localStorage에 저장만 해두고 /signup으로 보낸다
  // (2026-09-08) — 가입 완료 후 언젠가 이 화면에 왔을 때 ?ref=가 없으면 그 저장값을 대신 읽는다.
  // 한 번 읽은 코드는 useEffect에서 즉시 지운다(1회성 소비 — 나중에 다른 계정으로 다시 왔을 때
  // 같은 코드가 실수로 재적용되지 않도록). lazy initializer라 마운트 시 딱 한 번만 읽는다.
  const [{ code: referralCode, fromStorage }] = useState<{ code: string | undefined; fromStorage: boolean }>(() => {
    const fromUrl = searchParams.get('ref');
    if (fromUrl) return { code: fromUrl, fromStorage: false };
    try {
      const stored = localStorage.getItem(REFERRAL_CODE_STORAGE_KEY);
      return stored ? { code: stored, fromStorage: true } : { code: undefined, fromStorage: false };
    } catch {
      return { code: undefined, fromStorage: false }; // 프라이빗 모드 등 저장소 접근 불가 — 코드 없이 진행
    }
  });

  useEffect(() => {
    if (!fromStorage) return;
    try {
      localStorage.removeItem(REFERRAL_CODE_STORAGE_KEY);
    } catch {
      // 저장소 접근 불가 — 무시(어차피 이번 렌더에서 이미 값을 읽어 썼음)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trialMonths = getTrialMonths(Boolean(referralCode));

  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');

  // 페이지 최초 1회만 호출, 토글 전환은 클라이언트에서만 재렌더
  const plansQuery = useQuery({
    queryKey: ['billing', 'pricingPlans'],
    queryFn: async ({ signal }) => (await billingApi.getPricingPlans(signal)).data.data,
  });

  // 로그인 사용자만, 최초 1회
  const subscriptionQuery = useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: async ({ signal }) => (await billingApi.getSubscription(signal)).data.data,
    enabled: isLoggedIn,
  });

  // 비로그인은 로그인 화면으로, 로그인 상태에서 첫 체험은 결제확인 화면(CheckoutPage, mode=trial)
  // 에서 확인·시작하도록 이동한다(사용자 요청, 2026-09-03) — 이 화면에서 바로 체험을 시작하지 않는다.
  function handleCtaClick(tierCode: TierCode) {
    const action = resolveCtaAction(tierCode, isLoggedIn, subscriptionQuery.data, billingCycle);
    if (action === 'current') return; // 버튼이 이미 disabled — 도달하지 않음

    if (action === 'guest') {
      navigate('/login');
      return;
    }
    if (action === 'trial') {
      const refParam = referralCode ? `&ref=${referralCode}` : '';
      navigate(`/agents/billing/checkout?tier=${tierCode}&mode=trial&cycle=${billingCycle}${refParam}`);
      return;
    }
    if (action === 'cycle-change') {
      // 같은 티어, 결제 주기만 변경 — 방향(월간↔연간) 무관하게 항상 예약(즉시결제 없음, 2026-09-10
      // 정책 변경 — 예전엔 월간→연간만 즉시 프로레이션 청구였음). TierChangePage(변경 요약 화면)를
      // 재사용해 예약 확인을 받는다.
      navigate(`/agents/billing/change-tier?tier=${tierCode}&reserveCycle=${billingCycle}`);
      return;
    }
    // 티어 변경과 동시에 결제 주기 토글도 지금 구독과 다르게 맞춰져 있으면(ACTIVE·non-FREE 유료
    // 구독만 해당 — resolveCtaAction의 cycle-change 판정과 동일 기준) 티어만 바꾸고 토글 선택을
    // 조용히 무시하지 않는다 — TierChangePage의 티어+주기 동시 변경(예약, reserveCombinedChange)
    // 화면으로 보내 둘 다 반영되게 한다(2026-09-10, 사용자 요청 — 예전엔 이 화면의 토글이 업그레이드/
    // 다운그레이드 클릭 시 그냥 무시됐음).
    const cycleAlsoChanged =
      subscriptionQuery.data?.subStatus === 'ACTIVE' &&
      subscriptionQuery.data.tierCode !== 'FREE' &&
      subscriptionQuery.data.billingCycle != null &&
      subscriptionQuery.data.billingCycle !== billingCycle;

    if (action === 'upgrade') {
      if (cycleAlsoChanged) {
        navigate(`/agents/billing/change-tier?tier=${tierCode}&cycle=${billingCycle}`);
        return;
      }
      // cycleAlsoChanged가 false인 이 경로는 기존 ACTIVE 구독 주기와 토글이 같을 때뿐 아니라, FREE
      // 티어나 결제 이력은 있지만 지금 구독 자체가 없는 경우(예: 1회성 결제 만료 후 FREE로 복귀)도
      // 포함한다 — 이런 "기존 결제 주기가 없는/의미 없는" 첫 유료 전환은 예약 없이 즉시 결제되므로
      // 그대로 CheckoutPage로 보내되, 토글에서 고른 주기를 반드시 실어 보낸다(2026-09-15 발견·수정 —
      // 예전엔 이 값을 안 넘겨서 연간을 고르고 업그레이드해도 첫 결제가 항상 월간으로 나갔음).
      navigate(`/agents/billing/checkout?tier=${tierCode}&cycle=${billingCycle}`);
      return;
    }
    // downgrade
    if (cycleAlsoChanged) {
      navigate(`/agents/billing/change-tier?tier=${tierCode}&cycle=${billingCycle}`);
      return;
    }
    navigate(`/agents/billing/change-tier?tier=${tierCode}`);
  }

  const plans = plansQuery.data;
  const earlybird = plans?.activePromotions.find((p) => p.promotionType === 'EARLYBIRD');
  const subscription = subscriptionQuery.data;

  return (
    <Page width="xl">
      {/* 프로모션 배너·요금제 카드만 API 데이터에 의존한다. 히어로·토글·직원요금 안내·
          신뢰정보는 정적 요소라 pricing-plans 호출이 실패해도 그대로 노출한다. */}
      {earlybird && (
        <Alert
          kind="info"
          title="🎉 얼리버드 · 출시 초기 200명 한정"
          action={
            <Button variant="ghost" size="sm" onClick={() => navigate('/agents/billing/earlybird')}>
              신청하러 가기
            </Button>
          }
          className="mb-06"
        >
          연 구독 <strong>{earlybird.discountRateAnnual}%</strong> 할인 · 월 구독{' '}
          <strong>{earlybird.discountRateMonthly}%</strong> 할인
        </Alert>
      )}

      <div className="mx-auto max-w-page-md text-center">
        <p className="text-caption font-semibold uppercase tracking-wide text-group">PRICING</p>
        <h1 className="mt-04 text-display-sm font-bold text-text">
          사장님께 맞는
          <br />
          요금제를 골라보세요
        </h1>
        <p className="mt-04 text-body-md text-text-muted">
          {trialMonths}개월 무료체험 · 카드 등록 필요 없음 · 언제든 해지 가능
        </p>
      </div>

      <div className="mt-07 flex justify-center">
        <Segmented
          options={BILLING_CYCLE_OPTIONS}
          value={billingCycle}
          onChange={(v) => setBillingCycle(v as BillingCycle)}
        />
      </div>

      {plansQuery.isLoading && <PricingCardsSkeleton />}

      {plansQuery.isError && (
        <ErrorState className="mt-07" title="요금제 정보를 불러오지 못했습니다" />
      )}

      {plans && (
        <div className={`mt-07 ${PRICING_GRID_CLASS}`}>
          {TIER_ORDER.map((tierCode) => {
            const tier = plans.tiers.find((t) => t.tierCode === tierCode);
            if (!tier) return null;
            const state = resolveCtaAction(tierCode, isLoggedIn, subscription, billingCycle);
            const trialDaysLeft =
              state === 'current' ? calcTrialDaysLeft(subscription?.trialEndDtm ?? null) : null;
            return (
              <PricingCard
                key={tierCode}
                tier={tier}
                copy={TIER_COPY[tierCode]}
                billingCycle={billingCycle}
                freeMonthsAnnual={FREE_MONTHS_ANNUAL}
                state={state}
                trialDaysLeft={trialDaysLeft}
                hasEverPaid={subscription?.hasEverPaid ?? false}
                isTrialing={subscription?.subStatus === 'TRIAL'}
                trialMonths={trialMonths}
                onCtaClick={handleCtaClick}
              />
            );
          })}
        </div>
      )}

      <p className="mt-06 text-center text-body-sm text-text-muted">
        위 가격은 직원 3명까지 포함한 기본 요금이에요. 4명부터는 인원 구간별로 추가요금이 붙어요.{' '}
        <Button variant="ghost" size="sm" onClick={() => navigate('/agents/billing/staff-fee')}>
          자세히 보기 →
        </Button>
      </p>

      <div className="mt-08 grid grid-cols-1 gap-md border-t border-border pt-07 sm:grid-cols-2 xl:grid-cols-4">
        <StatusHint tone="info">카드·계좌이체·가상계좌·세금계산서</StatusHint>
        <StatusHint tone="success">7일 이내 미사용 시 전액환불</StatusHint>
        <StatusHint tone="info">체험 종료 전 알림으로 미리 안내</StatusHint>
        <StatusHint tone="muted">언제든 해지 가능</StatusHint>
      </div>
    </Page>
  );
}
